import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { saveAs } from 'file-saver';
import { useSnackbar } from '../../../components/snackbar';
import { Upload } from '../../../components/upload';
import { ProductService } from '../../../api/product.service';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const REQUIRED_COLUMNS = [
  'Name',
  'SKU',
  'Type',
  'Category',
  'Purchase Price',
  'Sale Price',
  'VAT Rate',
];

function getImportErrorPayload(error: any): {
  errors: string[];
  snackbarMessage: string;
} {

  const data = error?.response?.data ?? error?.data ?? error;

  const rowErrors = Array.isArray(data?.errors)
    ? data.errors.filter((item: unknown) => typeof item === 'string' && item.trim())
    : [];

  if (rowErrors.length > 0) {
    return {
      errors: rowErrors,
      snackbarMessage:
        typeof data?.message === 'string' && data.message.trim()
          ? data.message.trim()
          : rowErrors[0],
    };
  }

  if (typeof data?.message === 'string' && data.message.trim()) {
    return { errors: [data.message.trim()], snackbarMessage: data.message.trim() };
  }

  if (Array.isArray(error?.message)) {
    const messages = error.message.filter(
      (item: unknown) => typeof item === 'string' && item.trim(),
    );
    if (messages.length) {
      return { errors: messages, snackbarMessage: messages[0] };
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return { errors: [error.message.trim()], snackbarMessage: error.message.trim() };
  }

  return {
    errors: ['Failed to import products'],
    snackbarMessage: 'Failed to import products',
  };
}

export default function ProductImportDialog({ open, onClose, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const handleDrop = (acceptedFiles: File[]) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFile(newFile);
      setImportErrors([]);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const service = new ProductService();
      const res = await service.downloadImportTemplate();
      saveAs(res.data as Blob, 'product-import-template.xlsx');
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || 'Failed to download import template',
        { variant: 'error' },
      );
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setImportErrors([]);
      const service = new ProductService();
      const res = await service.importProducts(file);
      const data = res.data as {
        message?: string;
        errors?: string[];
        failed?: number;
      };

      if (data?.failed && data.failed > 0 && data.errors?.length) {
        setImportErrors(data.errors);
        enqueueSnackbar(data.message || 'Import completed with errors', {
          variant: 'warning',
        });
        onSuccess();
        return;
      }

      enqueueSnackbar(data?.message || 'Products imported successfully', {
        variant: 'success',
      });
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error(error);
      const { errors, snackbarMessage } = getImportErrorPayload(error);
      setImportErrors(errors);
      enqueueSnackbar(snackbarMessage, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportErrors([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Import Products</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Upload a CSV or Excel file with a header row. Required columns:{' '}
            {REQUIRED_COLUMNS.join(', ')}.
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            See the reference sheet in the template
          </Typography>

          <Button
            variant="outlined"
            size="small"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            sx={{ alignSelf: 'flex-start' }}
          >
            {downloadingTemplate ? 'Downloading…' : 'Download import template'}
          </Button>

          <Upload
            file={file}
            onDrop={handleDrop}
            onDelete={() => {
              setFile(null);
              setImportErrors([]);
            }}
            accept={{
              'text/csv': ['.csv'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                '.xlsx',
              ],
              'application/vnd.ms-excel': ['.xls'],
            }}
          />

          {importErrors.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Import issues
              </Typography>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {importErrors.slice(0, 10).map((err, index) => (
                  <Typography key={`${index}-${err}`} component="li" variant="body2">
                    {err}
                  </Typography>
                ))}
              </Stack>
              {importErrors.length > 10 && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  …and {importErrors.length - 10} more. Download the{' '}
                  <Link component="button" onClick={handleDownloadTemplate}>
                    template
                  </Link>{' '}
                  for valid type/category values.
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleUpload}
          variant="contained"
          loading={uploading}
          disabled={!file}
        >
          Import
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
